import * as React from "react";
import FilePreview, { PreviewNote, type PreviewState } from "./FilePreview";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Spinner } from "components";

interface KycDocument {
    id: string;
    name: string;
    url: {
        url: string;
    };
    status: "Unverified" | "Verified" | "Requested";
}

interface KycReviewModalProps {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    documents: KycDocument[];
    orderId: string;
    customerId: number;
    onApprove: () => void;
    isLoading?: boolean;
}

export default function KycReviewModal({
    isOpen,
    setIsOpen,
    documents,
    orderId,
    customerId: _customerId,
    onApprove: _onApprove,
    isLoading
}: KycReviewModalProps) {
  const openerRef = React.useRef<HTMLElement | null>(null);
  const wasOpenRef = React.useRef(false);
  if (isOpen && !wasOpenRef.current) openerRef.current = document.activeElement as HTMLElement | null;
  wasOpenRef.current = isOpen;
    const [currentDocIndex, setCurrentDocIndex] = React.useState(0);
    const handlePrevious = () => {
        if (currentDocIndex > 0) {
            setCurrentDocIndex(currentDocIndex - 1);
        }
    };

    const handleNext = () => {
        if (currentDocIndex < documents.length - 1) {
            setCurrentDocIndex(currentDocIndex + 1);
        }
    };

    const currentDoc = documents[currentDocIndex];
    // Approve waits until the file has actually been shown.
    const [preview, setPreview] = React.useState<{ key: string; state: PreviewState }>({ key: "", state: "loading" });
    const previewKey = `${currentDoc?.id}:${currentDoc?.url?.url ?? ""}`;
    const previewLoaded = preview.key === previewKey && preview.state === "loaded";

    if (!currentDoc) return null;

    return (
        <>
            {isLoading && <Spinner />}
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogPortal>
                    <DialogOverlay className="bg-black-900" />
                    <DialogContent className="max-w-4xl flex flex-col gap-0 p-0 max-h-[92vh] overflow-hidden bg-white-A700 rounded-md"
 onCloseAutoFocus={(e) => { if (openerRef.current?.isConnected) { e.preventDefault(); openerRef.current.focus(); } }}>
                        <DialogHeader className="flex shrink-0 flex-row items-center justify-between px-4 pb-3 pt-6">
                            <DialogTitle>KYC Document Review</DialogTitle>
                            <DialogClose asChild>Close</DialogClose>
                        </DialogHeader>
                        <div className="grid min-h-0 flex-1 grid-cols-[300px_1fr] md:grid-cols-1 gap-4 overflow-y-auto px-4 pb-2">
                            {/* Sidebar */}
                            <div className="space-y-2">
                                {documents.map((doc, index) => (
                                    <div
                                        role="button"
                                        key={doc.id}
                                        className={cn(
                                            "flex items-center justify-between p-2 rounded-lg cursor-pointer",
                                            currentDocIndex === index && "bg-muted text-blue-700",
                                        )}
                                        onClick={() => {
                                            if (index !== currentDocIndex) {
                                            }
                                            setCurrentDocIndex(index);
                                        }}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="text-sm font-medium font-inter leading-[16.9px] border rounded-md py-1 px-2.5 border-[#EFF4FF]">
                                                {index + 1}
                                            </span>
                                            <span className="text-sm font-medium font-inter leading-[16.9px]">
                                                {doc.name}
                                            </span>
                                        </div>
                                        {doc.status.toLowerCase() === "verified" && (
                                            <svg
                                                width="13"
                                                height="13"
                                                viewBox="0 0 13 13"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M6.76758 0.5C3.46158 0.5 0.767578 3.194 0.767578 6.5C0.767578 9.806 3.46158 12.5 6.76758 12.5C10.0736 12.5 12.7676 9.806 12.7676 6.5C12.7676 3.194 10.0736 0.5 6.76758 0.5ZM9.63558 5.12L6.23358 8.522C6.14958 8.606 6.03558 8.654 5.91558 8.654C5.79558 8.654 5.68158 8.606 5.59758 8.522L3.89958 6.824C3.72558 6.65 3.72558 6.362 3.89958 6.188C4.07358 6.014 4.36158 6.014 4.53558 6.188L5.91558 7.568L8.99958 4.484C9.17358 4.31 9.46158 4.31 9.63558 4.484C9.80958 4.658 9.80958 4.94 9.63558 5.12Z"
                                                    fill="#165FE3"
                                                />
                                            </svg>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Main Content */}
                            <div className="flex flex-col h-full">
                                <div className="flex flex-1 flex-col gap-2">
                                    <FilePreview
                                        key={previewKey}
                                        url={currentDoc?.url?.url}
                                        kind={(currentDoc?.url as { type?: string } | undefined)?.type === "pdf" ? "pdf" : "image"}
                                        name={currentDoc?.name ?? "Document"}
                                        onState={(state) => setPreview({ key: previewKey, state })}
                                    />
                                    {currentDoc?.url?.url ? <PreviewNote state={preview.key === previewKey ? preview.state : "loading"} /> : null}
                                </div>

                            </div>
                        </div>
                        <div className="shrink-0 border-t bg-white-A700 px-4 pb-4 pt-1">
                                <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
                                    <div className="flex flex-wrap gap-2">
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            aria-label="Previous document"
                                            onClick={handlePrevious}
                                            disabled={currentDocIndex === 0}
                                        >
                                            <ChevronLeft className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            aria-label="Next document"
                                            onClick={handleNext}
                                            disabled={currentDocIndex === documents.length - 1}
                                        >
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-4">
                                        <Button
                                            className="bg-blue-700 disabled:opacity-40 rounded-md text-white-A700 border border-blue-700"
                                            onClick={_onApprove}
                                            disabled={
                                                currentDoc?.status.toLowerCase() === "verified" ||
                                                !previewLoaded ||
                                                documents?.length === 0
                                            }
                                        >
                                            Approve
                                        </Button>
                                    </div>
                                </div>
                        </div>
                    </DialogContent>
                </DialogPortal>
            </Dialog>
        </>
    );
}
